import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, Loader2 } from "lucide-react";

const ICONS = { free: Zap, pro: Crown, enterprise: Crown };

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/pricing/plans").then((r) => setPlans(r.data));
  }, []);

  const checkout = async (planId) => {
    if (planId === "free") {
      toast.info("You're on the Free plan by default.");
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  if (!plans)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );

  const order = ["free", "pro", "enterprise"];

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <PageHeader
        tag="Upgrade"
        title="Choose your protection tier"
        subtitle="Scale your defense grid. Cancel anytime. Powered by Stripe (test mode)."
        right={
          <div className="font-mono text-sm border border-primary/50 bg-primary/10 text-primary px-4 py-2">
            current: {user?.plan || "free"}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
        {order.map((id, i) => {
          const p = plans[id];
          const Icon = ICONS[id];
          const isCurrent = (user?.plan || "free") === id;
          const featured = id === "pro";
          return (
            <div
              key={id}
              data-testid={`plan-${id}`}
              className={`relative bg-card p-8 flex flex-col animate-fade-up ${featured ? "ring-1 ring-primary" : ""}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {featured && (
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest px-3 py-1">
                  Popular
                </div>
              )}
              <Icon size={24} strokeWidth={1.5} className={featured ? "text-primary" : "text-muted-foreground"} />
              <h3 className="font-mono font-bold text-xl tracking-tight mt-4">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-mono font-bold text-4xl text-foreground">${p.amount}</span>
                <span className="font-mono text-sm text-muted-foreground mb-1">/{p.period}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <Check size={16} strokeWidth={2} className="text-primary mt-0.5 shrink-0" />
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`checkout-${id}`}
                disabled={busy === id || isCurrent}
                onClick={() => checkout(id)}
                className={`mt-8 rounded-none font-mono ${
                  featured
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
                    : "bg-secondary text-foreground hover:bg-accent border border-border"
                }`}
              >
                {busy === id ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isCurrent ? (
                  "Current plan"
                ) : id === "free" ? (
                  "Free forever"
                ) : (
                  `Subscribe to ${p.name}`
                )}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="mt-6 font-mono text-xs text-muted-foreground">
        Test card: 4242 4242 4242 4242 · any future expiry · any CVC.
      </p>
    </div>
  );
}
